import { map } from 'lodash';
import { ChildCollection } from 'graph/NodeChildCollection';
import { DIFF_TYPE } from '@ardoq/global-consts';
import { DiffType } from '@ardoq/data-model';
import { GraphItem, GraphItemModel } from './GraphItem';
import { Edge } from './edge';
import type {
  GraphItemDestroyOptions,
  ComponentDataModel,
} from './graphItemTypes';
import type { Node } from './types';
import { APIWorkspaceAttributes } from '@ardoq/api-types';

let idCounter = 0;
function uniqueId() {
  return `n${idCounter++}`;
}

/**
 * Base implementation of Node interface
 * Provides common functionality for all node types
 */
export abstract class BaseNode<TModel = GraphItemModel>
  extends GraphItem<TModel>
  implements Node<TModel>
{
  readonly id: string = uniqueId();
  included: boolean = true;
  excluded: boolean = false;
  treeDepth: number | undefined = undefined;
  dataModel: TModel;
  isTransparentized: boolean = false;

  children: ChildCollection;
  parentRelations: Record<string, GraphItemModel>;
  incomingEdges: Edge[];
  outgoingEdges: Edge[];
  aggregatedIncomingEdges: Edge[];
  aggregatedOutgoingEdges: Edge[];
  parent: Node<GraphItemModel> | null = null;

  constructor({
    dataModel,
    parent,
  }: {
    dataModel: TModel;
    parent?: Node<GraphItemModel>;
  }) {
    super();
    this.id = uniqueId();
    this.dataModel = dataModel;

    if (parent) {
      this.setParent(parent);
    }

    this.children = new ChildCollection([], {
      parent: this as Node<GraphItemModel>,
    });
    this.parentRelations = {};
    this.incomingEdges = [];
    this.outgoingEdges = [];
    this.aggregatedIncomingEdges = [];
    this.aggregatedOutgoingEdges = [];
  }

  // Abstract methods to be implemented by concrete classes
  abstract getShape(): string;
  abstract getCSS(options?: any): string;

  // Type checking methods
  /* virtual */ isComponent(): this is Node<ComponentDataModel> {
    return false;
  }

  // References are always `Edge`s, never nodes.
  isReference(): boolean {
    return false;
  }

  /* virtual */ isWorkspace(): this is Node<APIWorkspaceAttributes> {
    return false;
  }

  /* virtual */ isTag(): boolean {
    return false;
  }

  /* virtual */ isGroup(): boolean {
    return false;
  }

  /* virtual */ isField(): boolean {
    return false;
  }

  /* virtual */ getValue(): any {
    return null;
  }

  /* virtual */ name(): string {
    return '';
  }

  /* virtual */ getCSSFilterColor(): string | undefined {
    return undefined;
  }

  // Filter and context methods
  /* virtual */ isIncludedInContextByFilter(): boolean {
    return false;
  }

  /* virtual */ isColoredByFilter(): boolean {
    return false;
  }

  /* virtual */ hasURLFields(): boolean {
    return false;
  }

  /* virtual */ getImage(): string | null | undefined {
    return undefined;
  }

  // Display methods
  getLabel(): string {
    return this.name();
  }

  getVisualDiffType = (): DiffType => {
    const dataModel = this.dataModel as any;
    return (
      (dataModel &&
        typeof dataModel.get === 'function' &&
        dataModel.get(DIFF_TYPE)) ||
      DiffType.NONE
    );
  };

  // Hierarchy methods
  setParent(parent: Node<GraphItemModel>): void {
    this.parent = parent;
  }

  getTreeDepth(): number {
    if (this.treeDepth === undefined) {
      this.treeDepth = this.parent ? this.parent.getTreeDepth() + 1 : 0;
    }
    return this.treeDepth;
  }

  hasChildren(): boolean {
    return this.children && this.children.size() > 0;
  }

  getRootParent(): Node<GraphItemModel> {
    return this.parent
      ? this.parent.getRootParent()
      : (this as unknown as Node<GraphItemModel>);
  }

  // Edge management
  getIncomingEdges(): Edge[] {
    return this.incomingEdges;
  }

  getOutgoingEdges(): Edge[] {
    return this.outgoingEdges;
  }

  getAggregatedIncomingEdges(): Edge[] {
    return this.aggregatedIncomingEdges;
  }

  getAggregatedOutgoingEdges(): Edge[] {
    return this.aggregatedOutgoingEdges;
  }

  findOutgoingEdgesTo(targetNode: Node): Edge[] {
    return this.outgoingEdges.filter(edge => {
      return edge.target === targetNode.id;
    });
  }

  findIncomingEdgesFrom(targetNode: Node): Edge[] {
    return this.incomingEdges.filter(edge => {
      return edge.source === targetNode.id;
    });
  }

  addIncomingEdge(edge: Edge): void {
    this.incomingEdges.push(edge);
  }

  removeIncomingEdge(edge: Edge): void {
    this.incomingEdges.splice(this.incomingEdges.indexOf(edge), 1);
  }

  addOutgoingEdge(edge: Edge): void {
    this.outgoingEdges.push(edge);
  }

  removeOutgoingEdge(edge: Edge): void {
    this.outgoingEdges.splice(this.outgoingEdges.indexOf(edge), 1);
  }

  addAggregatedIncomingEdge(edge: Edge): void {
    this.aggregatedIncomingEdges.push(edge);
  }

  removeAggregatedIncomingEdge(edge: Edge): void {
    this.aggregatedIncomingEdges.splice(
      this.aggregatedIncomingEdges.indexOf(edge),
      1
    );
  }

  addAggregatedOutgoingEdge(edge: Edge): void {
    this.aggregatedOutgoingEdges.push(edge);
  }

  removeAggregatedOutgoingEdge(edge: Edge): void {
    this.aggregatedOutgoingEdges.splice(
      this.aggregatedOutgoingEdges.indexOf(edge),
      1
    );
  }

  // Lifecycle methods
  destroy(options: GraphItemDestroyOptions = {}): void {
    const { ignoreNodeUpdates = false } = options;

    if (this.children.length) {
      map(this.children.toArray(), 'id').forEach(childId => {
        this.children.get(childId)?.destroy({});
      });
    }

    if (this.isGroup()) {
      const dataModel = this.dataModel as any;
      dataModel.destroy?.();
    }

    if (this.parent && !ignoreNodeUpdates) {
      this.parent.children.delete(this.id);
    }
  }
}
